import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';

import { Theme } from '@app/theme';
import {
  CriterionConfig,
  CriterionItemInfo,
  CriterionStateAndQuotaInfo,
  ParticipantField,
} from '@app/api';
import { useGetCriterionValueText } from 'lib/criteriaUtils';
import { toPercent } from 'lib/numbers';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  first: {
    borderTop: `1px solid ${theme.colors.grayBorder}`,
  },
  high: {
    color: theme.colors.greenAccessible,
  },
  low: {
    color: theme.colors.redAccessible,
  },
  emph: {
    fontWeight: 'bold',
  },
  forced: {
    color: theme.colors.blueAccessible,
  },
  disabled: {
    opacity: 0.5,
  },
}));

interface Props {
  criterionStateInfo: CriterionStateAndQuotaInfo;
  field?: ParticipantField;
  config?: CriterionConfig;
  fieldRef: string;
  selectedWhiteList?: string[];
}

export const CriterionConfigTableLines = ({
  criterionStateInfo,
  field,
  config,
  fieldRef,
  selectedWhiteList,
}: Props) => {
  const classes = useStyles();
  const getCriterionValueText = useGetCriterionValueText();

  const getItemText = (infos: CriterionItemInfo) => {
    const defaultText = String(infos.item.item);
    if (!field) return defaultText;
    const text = getCriterionValueText(infos.item.listValueItem, field);
    if (!text) return defaultText;
    return text;
  };

  const noWeight = selectedWhiteList && !selectedWhiteList.includes(fieldRef);
  const inWhiteList = selectedWhiteList && selectedWhiteList.includes(fieldRef);

  const getWeight = (infos: CriterionItemInfo) => {
    if (!!infos.required && infos.missing === 0) {
      return 0.0;
    }

    if (noWeight) {
      return 1.0;
    }

    if (infos.normalizedWeight) {
      return infos.normalizedWeight;
    }

    if (infos.normalizedWeight === 0) {
      return 0;
    }
    return 1.0;
  };

  const isForcedWeight = (infos: CriterionItemInfo) => {
    if (!!infos.required && infos.missing === 0) {
      return false;
    }
    if (!noWeight) {
      return false;
    }

    if (infos.normalizedWeight === 1) {
      return false;
    }

    if (
      infos.normalizedWeight === undefined ||
      infos.normalizedWeight === null
    ) {
      return false;
    }
    return true;
  };

  return (
    <>
      {criterionStateInfo.allItemsInfos.map((infos, index) => (
        <tr
          className={classNames({
            [classes.first]: index === 0,
            [classes.disabled]: !config?.enabled,
          })}
        >
          <th
            className={classNames({
              [classes.high]: inWhiteList,
            })}
          >
            {index === 0 ? field?.label || fieldRef : ''}
          </th>
          <td>{getItemText(infos)}</td>
          <td>{infos.missing ?? ''}</td>
          <td>{infos.missingWeight ? toPercent(infos.missingWeight) : ''}</td>
          <td> {infos.inPanelCount} </td>
          <td>{infos.inPanelWeight ? toPercent(infos.inPanelWeight) : ''}</td>
          <td
            className={classNames({
              [classes.high]: !isForcedWeight(infos) && getWeight(infos) > 0.8,
              [classes.low]: getWeight(infos) < 0.3,
              [classes.forced]: isForcedWeight(infos),
              [classes.emph]: inWhiteList,
            })}
          >
            {toPercent(getWeight(infos))}
          </td>
        </tr>
      ))}
      {config?.allowEmpty && !!criterionStateInfo.missingCountInPanel && (
        <tr
          className={classNames({
            [classes.disabled]: !config?.enabled,
          })}
        >
          <th></th>
          <td>Vide</td>
          <td></td>
          <td></td>
          <td>{criterionStateInfo.missingCountInPanel}</td>
          <td></td>
          <td
            className={classNames({
              [classes.forced]: inWhiteList,
            })}
          >
            {inWhiteList ? toPercent(0) : toPercent(config.emptyMalus)}
          </td>
        </tr>
      )}
    </>
  );
};
